import { createContext } from '@fluentui/react-context-selector';
import type {
  ApiScope,
  AttributeFilters,
  OasDocument3XExtended,
  SnapshotId,
  date,
} from '@ipfabric/utils';
import { ISO_8601_UTC_DATETIME_FORMAT } from '@ipfabric/utils/date';
import type { VerifiedLicense } from '@ipfabric/utils/license/licenseSchema';
import type { CreateAllRoutesFunction } from 'containers/App/Routes/createAllRoutes';
import type { LogErrorFunction } from 'containers/ErrorBoundary/useLogError';
import type { NavigateFunction } from 'react-router-dom';
import type { Dispatch, Store } from 'redux';
import type { AppTranslations } from 'translations/translations';
import type { IpfLocation } from 'utils/history/IpfLocation';
import type { NavigatePushPathFunction } from 'utils/history/useNavigatePushPath';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import type { ShowNotificationCommand } from 'utils/hooks/useNotifications';
import type { PermissionScope } from 'utils/hooks/usePermissionScope';
import {
  type ContextSelectorsHook,
  useContextSelectors,
} from 'utils/react/useContextSelectors';
import type { ApiHook } from 'utils/request/useApi';
import { globalAppThemeLocalStorage } from './globalAppThemeLocalStorage';
import { globalAttributeFiltersLocalStorage } from './globalAttributeFiltersLocalStorage';

/**
 * NOT_VERIFIED = initial state when app does not know license state yet
 * ERROR = checking for license validity returned an unexpected error
 * VALID = license is valid and not expired
 * NOT_FOUND = license was checked but not found
 * EXPIRED = license was found, technically valid, but after expiration date
 */
export type LicenseState =
  | 'NOT_VERIFIED'
  | 'ERROR'
  | 'VALID'
  | 'NOT_FOUND'
  | 'EXPIRED';

// UNINITIALIZED - initial state after page load. It is used before check if user is logged in.
// It is set as default value for loginStatus.
// LOGGED_OUT - user is not logged in. It is used after user does logout manually or app get response 401 from
// server and refresh token is not valid or there is no refresh token or some other error in auth flow.
// LOGGING_IN - user is being authenticated. This state was introduced due to race condition bug
// where redirect after login was faster than context update, so RequireAuth component
// sometimes immediately logged user out after login because it was still seeing old LOGGED_OUT value.
// AUTHENTICATED - user is logged in and application has all data for proper work
// like information about user, versions, roles etc.
export type LoginStatus =
  | 'UNINITIALIZED'
  | 'LOGGED_OUT'
  | 'LOGGING_IN'
  | 'AUTHENTICATED';

export type AppThemeState = 'light' | 'dark';

export interface LicenseInfoApiResponse {
  devices: number | null;
  ccu?: number | null;
  validTo: number | null;
  expired: boolean;
  additionalFeatures?: VerifiedLicense['additionalFeatures'];
}

export interface LicenseValidityApiResponse {
  validTo: number | null;
  expired: boolean;
  additionalFeatures?: VerifiedLicense['additionalFeatures'];
}

export interface License {
  devices: number | null;
  ccu?: number | null;
  validTo: number | null;
  state: LicenseState;
  additionalFeatures?: VerifiedLicense['additionalFeatures'];
}

export interface GlobalUserState {
  username: string | null;
  timezone: date.Timezone;
  ldapId: string | null;
  isLocal: boolean;
  theme: AppThemeState;
  pinnedPages: string[];
}

export interface GlobalAuthState {
  /** User ID */
  id: string | null;
  /** Scope - User permissions */
  scope: PermissionScope[];
  apiScopes: ApiScope[];
  admin: boolean;
  roleIds: string[];
}

/**
 * App socket connection state
 *
 * UNINITIALIZED = initial app state before first attempt to establish socket connection.
 */
export type SocketState = 'UNINITIALIZED' | 'CONNECTED' | 'DISCONNECTED';

/**
 * Internal component state managed by the command model
 */
export interface GlobalStateState {
  /**
   * Indicate error occurred during initial app loading.
   *
   * We don't track whether app is still loading in state as it is redundant.
   * App is loading when selectAppInitialized returns false and appLoadError is still false too.
   */
  appLoadError: boolean;
  attributeFilters: AttributeFilters;
  auth: GlobalAuthState;
  contentWrapper: HTMLElement | null;
  contentAfterContainer: HTMLElement | null;
  dateFormat: string;
  dateTimeFormat: string;
  isoFormat: string;
  ipfNewVersion: string | null;
  license: License;
  loginStatus: LoginStatus;
  maintenanceRunning: boolean;
  minimisedMenu: boolean;
  oas: OasDocument3XExtended | null;
  refreshingTokensPromise: Promise<void> | null;
  socketState: SocketState;
  /**
   * API server is unavailable, e.g. during system upgrade or restart.
   * Set only after the outage is confirmed by probing the API, see `reportSuspectedOutage`.
   */
  systemUnavailable: boolean;
  user: GlobalUserState;
  version: string | null;
}

/**
 * External resources that will be available to commands (API handlers etc)
 */
export interface GlobalStateAdditionalCommandContext {
  api: ApiHook;
  logError: LogErrorFunction;
  navigate: NavigateFunction;
  navigatePushPath: NavigatePushPathFunction;
  reduxDispatch: Dispatch<any>;
  reduxStore: Store<any>;
  showNotification: (command: ShowNotificationCommand) => void;
}

/**
 * External data available to the component
 */
export interface GlobalStateOuterState {
  createAllRoutes: CreateAllRoutesFunction;
  /**
   * Whether the current snapshot supports the new cloud licensing model, `undefined` when
   * nothing is known about it yet
   */
  cloudLicensingSupported: boolean | undefined;
  location: IpfLocation;
  msg: AppTranslations;
  snapshotId: SnapshotId | null;
}

// Let's create short aliases because all 3 following types are
// repeatedly used to create specific types for given command model.
type S = GlobalStateState;
type C = GlobalStateAdditionalCommandContext;
type O = GlobalStateOuterState;

// specific types for given command model, to avoid using the three-argument generic widely in userland
// See doc comment for given generic types to understand what they are good for.
export type GlobalStateCommandContext = CommandModelCommandContext<S, C, O>;
export type GlobalStateCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<S, C, O, TCommandArgs>;
export type GlobalStateDispatch = CommandModelCommandDispatcher<S, C, O>;
export type GlobalStateMergedState = CommandModelMergedState<S, O>;
export type GlobalStateModel = CommandModel<S, C, O>;

export const EMPTY_USER: GlobalUserState = {
  username: null,
  timezone: 'UTC',
  ldapId: null,
  isLocal: true,
  theme: globalAppThemeLocalStorage.get(),
  pinnedPages: [],
};

export const EMPTY_AUTH: GlobalAuthState = {
  id: null,
  scope: [],
  apiScopes: [],
  roleIds: [],
  admin: false,
};

export const globalStateInitialState: GlobalStateState = {
  appLoadError: false,
  attributeFilters: globalAttributeFiltersLocalStorage.get(),
  auth: EMPTY_AUTH,
  contentWrapper: null,
  contentAfterContainer: null,
  dateFormat: 'yyyy-MM-dd',
  dateTimeFormat: 'yyyy-MM-dd, HH:mm:ss X',
  isoFormat: ISO_8601_UTC_DATETIME_FORMAT,
  ipfNewVersion: null,
  license: {
    devices: null,
    ccu: undefined,
    validTo: null,
    state: 'NOT_VERIFIED',
    additionalFeatures: undefined,
  },
  loginStatus: 'UNINITIALIZED',
  maintenanceRunning: false,
  minimisedMenu: false,
  oas: null,
  refreshingTokensPromise: null,
  socketState: 'UNINITIALIZED',
  systemUnavailable: false,
  user: EMPTY_USER,
  version: null,
};

/**
 * Context to provide the hook to react components subtree to avoid prop-drilling
 *
 * The context must be created by `createContext` from `@fluentui/react-context-selector`
 * to allow using context selectors, so that all components consuming the context don't have to rerender
 * on every model change.
 *
 * Such context should be consumed with `useContextSelectors` function, resp. its specific variant
 * `useGlobalStateContextSelectors` below.
 *
 * No default value:
 * https://medium.com/@rivoltafilippo/typing-react-context-to-avoid-an-undefined-default-value-2c7c5a7d5947
 */
export const GlobalStateContext = createContext<GlobalStateModel>(undefined!);

export const useGlobalStateContextSelectors: ContextSelectorsHook<
  GlobalStateModel
> = (selectors) => useContextSelectors(GlobalStateContext, selectors);
